/** POS sync: bills and other commands from POS devices that need a person (docs/specs/pos-sync.md). */
export const pos = {
    report: {
        x: "X-REPORT",
        z: "Z-REPORT · SHIFT CLOSED",
        opened: "Opened",
        closed: "Closed",
        by: "By",
        float: "Opening cash",
        bills: "Bills",
        returns: "Returns",
        sales: "Sales",
        discounts: "Discounts",
        vat: "VAT",
        rounding: "Rounding",
        payments: "Payments",
        credit: "On credit",
        cashIn: "Cash in",
        cashOut: "Cash out",
        expected: "Expected cash",
        counted: "Counted cash",
        variance: "Variance",
        denominations: "Count",
        note: "Note",
        refunds: "Refunds",
        toDues: "Off customers' dues",
        exchanges: "Exchanges",
        reasons: {
            petty_expense: "Petty expense",
            bank_drop: "Bank drop",
            float_top_up: "Float top-up",
            other: "Other",
        },
    },
    receipt: {
        bill: "Bill",
        date: "Date",
        cashier: "Cashier",
        customer: "Customer",
        bin: "BIN {{bin}}",
        subtotal: "Subtotal",
        discount: "Discount",
        vat: "VAT",
        vatIncluded: "VAT included",
        rounding: "Rounding",
        total: "Total",
        paid: "Paid",
        change: "Change",
        onCredit: "On credit (due)",
        reprint: "REPRINT",
        thanks: "Thank you for shopping with us",
        methods: { cash: "Cash", card: "Card", bkash: "bKash", nagad: "Nagad", rocket: "Rocket" },
        reference: "Ref {{ref}}",
        returnTitle: "RETURN",
        returnNumber: "Return no.",
        originalBill: "For bill",
        damaged: "damaged",
        refundTotal: "Refund",
        toDues: "Off dues",
        exchangeFor: "Exchange → {{bill}}",
        exchangeCredit: "Exchange credit",
        reason: "Reason",
    },
    app: {
        name: "POS",
        starting: "Opening the POS…",
        startFailed: "The POS could not open its data on this computer.",
        language: "Language",
        english: "English",
        bengali: "বাংলা",
        shiftMenu: {
            title: "Shift",
            since: "Open since {{time}} · {{name}}",
            cash: "Cash in / out",
            xReport: "X-report",
            noSale: "Open drawer (no sale)",
            noSaleDone: "Drawer opened.",
            close: "Close shift",
            print: "Print",
        },
        cash: {
            title: "Cash in / out",
            in: "Cash in",
            out: "Cash out",
            amount: "Amount (৳)",
            reason: "Reason",
            note: "Note (optional)",
            save: "Record",
            saved: "Recorded. The drawer is open.",
        },
        close: {
            countTitle: "Count the cash",
            countHelp: "Count the drawer by notes and coins. The expected amount appears after the count.",
            counted: "Counted",
            next: "Next",
            summaryTitle: "Close the shift",
            over: "Over by {{amount}}",
            short: "Short by {{amount}}",
            balanced: "Balanced",
            note: "Note (optional)",
            confirm: "Close shift and print Z-report",
            closed: "Shift closed. Z-report printed.",
            closedNoPrint: "Shift closed.",
            back: "Back to count",
        },
        approval: {
            title: "Manager approval",
            reasons: {
                cash_out: "Taking cash out of the drawer needs a manager.",
                line_void: "Removing an item needs a manager.",
                bill_void: "Clearing the cart needs a manager.",
                no_sale: "Opening the drawer without a sale needs a manager.",
                refund_over_limit: "This refund is over the limit and needs a manager.",
                receiptless_return: "A return without a receipt needs a manager.",
                discount_over_limit: "This discount is over the limit and needs a manager.",
                price_override: "Changing the price needs a manager.",
                pin_unlock: "Unlocking needs a manager.",
            },
            approve: "Approve",
        },
        returns: {
            title: "Return / exchange",
            numberLabel: "Bill number",
            numberHelp: "Scan the barcode on the receipt or type the bill number.",
            find: "Find bill",
            withoutReceipt: "Return without receipt",
            notFound: "No bill {{number}} on this counter or the server.",
            offline: "Bill {{number}} is not on this counter and the server cannot be reached. Return it without a receipt.",
            beforeReturns: "This bill was made before returns were possible on this counter. Return it without a receipt.",
            billTitle: "Bill {{number}}",
            fromHere: "{{time}} · this counter",
            fromServer: "{{time}} · another counter",
            receiptlessTitle: "Return without receipt",
            receiptlessHelp: "Scan or search each item coming back. It is refunded at today's price.",
            sold: "Sold",
            left: "Left",
            back: "Back",
            all: "All",
            condition: { sellable: "Sellable", damaged: "Damaged" },
            reason: "Reason",
            reasons: {
                defective: "Defective",
                wrong_item: "Wrong item",
                changed_mind: "Changed mind",
                expired: "Expired",
                other: "Other",
            },
            note: "Note (optional)",
            value: "Refund value",
            nothing: "Choose what comes back.",
            refund: "Refund",
            exchange: "Exchange",
            exceeds: "Only {{left}} left to return on this line.",
            noPermission: "You cannot take returns. Ask a manager.",
            changeBill: "Another bill",
        },
        refund: {
            title: "Refund",
            value: "Value of the goods back",
            dues: "Off the customer's dues",
            exchange: "Paid towards the new bill",
            owed: "Still to give back",
            toDues: "Customer's dues",
            reference: "TrxID / slip number",
            add: "Add",
            complete: "Complete return",
            completeWith: "Complete return · {{amount}} {{method}}",
            remove: "Remove",
            errors: {
                NOTHING_OWED: "Nothing is left to give back.",
                AMOUNT_INVALID: "Enter an amount above zero.",
                MORE_THAN_OWED: "That is more than is left to give back.",
                REFERENCE_REQUIRED: "Enter the TrxID or slip number of the refund.",
                NEEDS_CUSTOMER: "Attach a customer first.",
                TOO_MANY_TENDERS: "Too many refund lines.",
                EMPTY: "Nothing is coming back.",
                NOT_SETTLED: "Give back the whole amount first.",
                APPROVAL_REQUIRED: "A manager must approve this return.",
                NO_BILL_PREFIX: "This branch has no bill prefix. Ask an admin to set it.",
                INVALID: "This return could not be saved. Check the amounts.",
            },
        },
        returnDone: {
            title: "Return complete",
            number: "Return {{number}}",
            giveBack: "Give back",
            toDues: "Taken off dues",
            exchangeCredit: "Paid towards the new bill",
            done: "Done",
        },
        exchange: {
            banner: "Exchange for {{bill}} · credit {{amount}}",
            bannerNoReceipt: "Exchange without receipt · credit {{amount}}",
            cancel: "Cancel exchange",
            credit: "Exchange credit",
            rest: "Refund the rest",
        },
        shift: {
            title: "Open the shift",
            description: "Count the cash already in the drawer before the first sale.",
            float: "Cash in drawer (৳)",
            open: "Open shift",
            opened: "Shift opened.",
        },
        pay: {
            title: "Payment",
            total: "To pay",
            paid: "Paid",
            due: "Still due",
            change: "Change",
            amount: "Amount (৳)",
            reference: "TrxID",
            cardReference: "Card slip / last 4 (optional)",
            add: "Add",
            exact: "Exact",
            complete: "Complete sale",
            completeWith: "Complete sale · {{amount}} {{method}}",
            completeExact: "Complete sale · {{amount}} {{method}} (exact)",
            exactHint: "Exact: {{amount}}",
            completing: "Saving…",
            remove: "Remove payment",
            methods: {
                cash: "Cash",
                card: "Card",
                bkash: "bKash",
                nagad: "Nagad",
                rocket: "Rocket",
                credit: "On credit",
            },
            noAccount: "No {{method}} account at this counter.",
            setupAccounts: "Cash needs a cash drawer for this counter; other methods need their account. Add them in Admin → Payments → Accounts, then sync.",
            creditLimit: "Limit {{limit}} · due now {{balance}}",
            pendingVerification: "Checked later against the {{method}} statement.",
            errors: {
                NOTHING_DUE: "Nothing is due.",
                AMOUNT_INVALID: "Enter an amount.",
                MORE_THAN_DUE: "More than what is due.",
                REFERENCE_REQUIRED: "Enter the TrxID (8–20 letters and digits).",
                TOO_MANY_TENDERS: "At most 10 payments per bill.",
                NEEDS_CUSTOMER: "Attach a customer first (F7).",
                OVER_CREDIT_LIMIT: "This would pass the customer's credit limit.",
                NOT_PAID: "The bill is not fully paid.",
                NO_BILL_PREFIX: "This branch has no bill prefix yet. Set it in Admin.",
                INVALID: "The sale could not be saved. Check the lines and try again.",
            },
        },
        done: {
            title: "Sale complete",
            change: "Give change",
            noChange: "No change",
            bill: "Bill {{number}}",
            printing: "Printing…",
            printed: "Receipt printed.",
            noPrinter: "No printer set up. Add one in printer settings.",
            printFailed: "Receipt not printed: {{message}}",
            retry: "Print again",
            next: "Next customer",
        },
        bills: {
            title: "Recent bills",
            empty: "No bills on this counter yet.",
            reprint: "Reprint",
            a4: "A4 invoice",
            reprinted: "Receipt reprinted.",
        },
        printers: {
            title: "Printers",
            empty: "No printer yet.",
            add: "Add printer",
            name: "Name",
            kind: "Connection",
            network: "Network (IP)",
            system: "Installed on this computer (USB)",
            address: "IP address",
            queue: "Printer",
            paper: "Paper",
            paperSize: "{{size}} mm",
            drawer: "Open the cash drawer for cash sales",
            makeDefault: "Use for receipts",
            isDefault: "Default",
            test: "Test print",
            openDrawer: "Open drawer",
            remove: "Remove",
            testOk: "Test page sent.",
            approve: "A manager's PIN is needed to change printers.",
        },
        sale: {
            scanPlaceholder: "Scan a barcode, or type to search (F2)",
            scanLabel: "Scan or search",
            cart: "Cart {{n}}",
            newCart: "New cart",
            emptyTitle: "Scan or search to start",
            emptyHelp: "Barcode scanners work any time. Type 3* before a scan to add three.",
            notFound: "No product with code {{code}}.",
            archived: "{{name}} is not for sale.",
            tooManyLines: "A bill can have at most 500 lines.",
            qtyOutOfRange: "That quantity is not possible.",
            tabsFull: "Nine carts are open. Hold or finish one first.",
            nothingToHold: "The cart is empty.",
            held: "Bill held.",
            notAllowed: "You are not allowed to sell at this counter.",
            noLongerInCatalog: "No longer in the catalog",
            columns: { item: "Item", qty: "Qty", price: "Price", discount: "Discount", total: "Total" },
            stock: "Stock {{qty}}",
            mrp: "MRP {{amount}}",
            searchEmpty: "No product matches “{{query}}”.",
            searchHint: "Type at least 2 letters.",
            quickKeys: "Quick keys",
            quickKeysEmpty: "No categories with products yet.",
            backToCategories: "All categories",
            totals: {
                subtotal: "Subtotal",
                lineDiscounts: "Item discounts",
                billDiscount: "Bill discount",
                vat: "VAT",
                total: "Total",
                items_one: "{{formatted}} item",
                items_other: "{{formatted}} items",
            },
            customer: {
                title: "Customer",
                none: "Walk-in customer",
                attach: "Add customer",
                change: "Change",
                remove: "Remove",
                phone: "Mobile number",
                name: "Name",
                find: "Find",
                create: "Add and attach",
                invalidPhone: "Enter a Bangladeshi mobile number.",
                notFoundNew: "No customer with this number yet. Add their name to create one.",
                due: "Due {{amount}}",
                created: "Customer added.",
            },
            note: { title: "Note", placeholder: "Note for this bill", add: "Add note", save: "Save note" },
            discount: {
                line: "Item discount",
                bill: "Bill discount",
                amount: "Amount (৳)",
                percent: "Percent (%)",
                value: "Discount",
                apply: "Apply",
                remove: "Remove discount",
                overLimit: "This is more than the {{limit}} allowed. A manager must approve.",
                approved: "Approved by {{name}}.",
            },
            qty: { title: "Quantity", label: "Quantity ({{unit}})", apply: "Set" },
            weight: { title: "Weigh {{name}}", label: "Weight ({{unit}})", add: "Add" },
            heldList: {
                title: "Held bills",
                empty: "No held bills.",
                resume: "Resume",
                heldAt: "Held {{time}}",
            },
            actions: {
                quantity: "Quantity",
                increase: "One more",
                decrease: "One less",
                discount: "Discount",
                billDiscount: "Bill discount",
                customer: "Customer",
                note: "Note",
                hold: "Hold",
                heldBills: "Held bills",
                remove: "Remove line",
                clear: "Clear cart",
                pay: "Pay",
                signOut: "Sign out",
                bills: "Recent bills",
                printers: "Printers",
                shift: "Shift",
                returns: "Return",
            },
            clearConfirm: "Remove every line from this cart?",
            closeCart: "Close {{name}}",
            closeTitle: "Close {{name}}?",
            closeBody: "Lines in this cart: {{lines}}. Hold it to finish later, or clear it — clearing needs a manager.",
            closeHold: "Hold for later",
            closeClear: "Clear and close",
        },
        sync: {
            suspended: "Subscription suspended — selling continues. Please tell the owner to pay the Sobthik plan.",
            downloading: "Downloading the shop's data…",
            downloadingHelp: "Needed once on this computer. Selling starts when it is done.",
            datasets: {
                settings: "Settings",
                staff: "Staff",
                payment_accounts: "Payment accounts",
                categories: "Categories",
                items: "Products",
                stock: "Stock",
                customers: "Customers",
            },
            rows: "{{done}} of {{total}}",
            waiting: "Waiting for the internet to continue…",
            online: "Online",
            offline: "Offline",
            lastSync: "Synced {{time}}",
            never: "Not synced yet",
            pending_one: "{{formatted}} to send",
            pending_other: "{{formatted}} to send",
            allSent: "All sent",
            sending: "Sending…",
            blocked: "Sync blocked",
            upgrade: "Update needed",
            stalled: "Not sent for 15+ min",
            details: "Sync details",
            status: "Connection",
            toSend: "Waiting to send",
            oldest: "Oldest waiting since",
            review: "Need review in Admin",
            lastPush: "Last sent",
            none: "—",
            syncNow: "Sync now",
            blockedHelp: "The server is waiting for command {{seq}} from this computer, which is missing here. Selling continues; ask a manager to resolve it in Admin → POS devices.",
            upgradeHelp: "The server needs a newer version of this app before it takes more bills. Selling continues and bills wait here safely.",
            stalledHelp: "Bills have waited over 15 minutes although the internet is up. Keep selling; if it stays like this, tell a manager.",
            offlineHelp: "Bills are saved on this computer and sent when the internet is back.",
            redownload: "Re-download data",
            redownloadHelp: "Products, prices, stock and staff are downloaded again. Bills on this computer stay.",
            redownloadStarted: "Downloading the data again…",
        },
        pairing: {
            title: "Pair this computer",
            description: "In Admin, open POS devices, make a pairing code for this register and type it here.",
            code: "Pairing code",
            submit: "Pair",
            syncing: "Pairing…",
            notFound: "That code is wrong, used or expired. Make a new one in Admin.",
            offline: "Pairing needs the internet. Check the connection and try again.",
            failed: "Pairing did not finish. Try again.",
            stepsTitle: "How to pair",
            step1: "In Admin, open POS devices.",
            step2: "Choose Pair a register, pick this counter and make a code.",
            step3: "Type the 8-character code here. It works once, for 10 minutes.",
            codeHint: "Letters and numbers; the dash is optional.",
            codePlaceholder: "XXXX-XXXX",
        },
        login: {
            title: "Who is selling?",
            noStaff: "No staff synced yet. Connect to the internet once so staff can sign in.",
            pin: "PIN for {{name}}",
            submit: "Sign in",
            back: "Back",
            wrongPin_one: "Wrong PIN. {{count}} try left before the lock.",
            wrongPin_other: "Wrong PIN. {{count}} tries left before the lock.",
            locked: "Locked after 5 wrong PINs until {{time}}.",
            noPin: "{{name}} has no POS PIN yet. Set it in Admin → Security, then sync.",
            managerUnlock: "Manager unlock",
            manager: "Manager",
            managerPin: "Manager PIN",
            unlock: "Unlock",
            unlocked: "Unlocked. Try the PIN again.",
            denied: "That manager cannot unlock, or the PIN is wrong.",
            chooseManager: "Choose a manager",
            subtitle: "Tap your name, then enter your PIN.",
            changePerson: "Not you?",
            noPinBadge: "No PIN",
            keypadClear: "Clear",
            keypadDelete: "Delete last digit",
            lockedTitle: "Locked for now",
        },
        updates: {
            ready: "Version {{version}} is ready",
            readyHelp: "Installing takes about a minute; the POS restarts and staff sign in again.",
            downloading: "Downloading version {{version}}…",
            install: "Install and restart",
            shiftOpen: "Close the shift first: updates never install during a shift.",
            required: "This version of the POS is no longer supported",
            requiredHelp: "Update to keep selling. Bills on this computer are kept.",
            requiredShift: "Finish and close the shift, then update. New sign-ins stop once the shift is closed.",
            requiredWaiting: "The update has not downloaded yet. Check the internet connection.",
            installFailed: "The update could not be installed. Try again.",
            betaJoin: "Join beta updates",
            betaLeave: "Leave beta updates",
            betaHelp: "Beta versions reach a few tills first. Use it only on tills a manager watches closely.",
            betaOn: "This till now gets beta updates.",
            betaOff: "This till now gets stable updates.",
        },
        protection: {
            unpair: "Unpair this computer",
            unpairHelp: "This computer stops being a till. Its data is kept in an archive here; pair it again, or pair another till, to carry on.",
            unpairConfirm: "Unpair",
            unpairPending_one: "{{formatted}} record has not reached the server. It stays in the archive and can be recovered after pairing again — sync first if the internet is up.",
            unpairPending_other: "{{formatted}} records have not reached the server. They stay in the archive and can be recovered after pairing again — sync first if the internet is up.",
            recoverTitle: "Unsent records from an earlier pairing",
            recoverBody_one: "Till {{code}} left {{formatted}} record that the server never received (archived {{time}}).",
            recoverBody_other: "Till {{code}} left {{formatted}} records that the server never received (archived {{time}}).",
            recover: "Recover now",
            recoverHelp: "They are sent as the old till and each is marked for a manager to check in Admin → POS review.",
            recoverOffline: "Connect to the internet to recover them.",
            recovered: "Recovered: {{processed}} sent, {{replayed}} were already on the server.",
            recoverBlocked: "The server is missing record {{seq}} of that till, and this archive does not have it. Ask the owner to check Admin → POS devices.",
            recoverFailed: "Recovery stopped. Check the internet and try again.",
            restoreTitle: "Restore from a backup",
            restoreHelp: "This computer's till data could not be opened. Restore the newest hourly backup to carry on as the same till.",
            restoreFrom: "Backup of {{time}}",
            restore: "Restore",
            restoreFailed: "The backup could not be restored.",
            signOutTitle: "Records not sent yet",
            signOut_one: "{{formatted}} record has not reached the server. It is safe on this computer and is sent when it is online.",
            signOut_other: "{{formatted}} records have not reached the server. They are safe on this computer and are sent when it is online.",
            signOutAnyway: "Sign out",
        },
        revoked: {
            title: "This POS was revoked",
            body: "A manager removed this computer. Bills that were not sent are kept safely on it; ask your manager to recover them.",
        },
    },
    /** SB-04: the browser POS of the "Try it yourself" demo (never in the desktop app). */
    demo: {
        badge: "Demo POS",
        internet: "Internet",
        on: "On",
        off: "Off",
        offlineHint: "Bills are kept on this computer and sync when the internet is back.",
        pin: "PIN for every cashier: {{pin}}",
        receiptTitle: "Receipt",
        receiptNote: "On a real counter this prints on the thermal printer.",
        close: "Close",
        pairingFailed: "This demo link has expired. Open the POS again from your demo shop's Admin.",
    },
    devices: {
        title: "POS devices",
        description: "Computers running the POS app, one per register. Revoking locks the device on its next contact.",
        empty: "No POS devices yet. Pair a register to start selling.",
        pair: "Pair a register",
        filters: { status: "Status", all: "All", active: "Active", revoked: "Revoked" },
        columns: {
            name: "Device",
            code: "Code",
            location: "Branch",
            platform: "System",
            pairedAt: "Paired",
            status: "Status",
            sync: "Sync",
            lastSeen: "Last seen",
            version: "Version",
            pending: "To send",
        },
        health: {
            online: "Online",
            offline: "Offline",
            stalled: "Not sending",
            never: "Never connected",
            gap: "Waiting for command {{seq}}",
            oldest: "oldest {{time}}",
        },
        stats: {
            online: "Online now",
            onlineHint: "of {{total}} active devices",
            stalled: "Not sending",
            stalledHint: "Online, bills waiting 15+ min",
            pending: "Bills to send",
            pendingHint: "Still on the counters",
        },
        revokePending_one: "This device still has {{formatted}} command it has not sent. Revoke only if the computer is lost or replaced, then recover its bills.",
        revokePending_other: "This device still has {{formatted}} commands it has not sent. Revoke only if the computer is lost or replaced, then recover its bills.",
        platforms: { windows: "Windows", macos: "macOS", linux: "Linux" },
        active: "Active",
        revoked: "Revoked",
        revoke: "Revoke",
        revokeTitle: "Revoke this device?",
        revokeBody: "It stops syncing and locks on its next contact. Bills it has not sent yet stay on the device and can be recovered.",
        revokedToast: "Device revoked.",
        pairing: {
            title: "Pair a register",
            description: "Make a one-time code, then type it into the POS app on that computer.",
            location: "Branch",
            register: "Register",
            name: "Device name",
            namePlaceholder: "Front counter PC",
            chooseLocation: "Choose a branch",
            chooseRegister: "Choose a register",
            noRegisters: "This branch has no free active register.",
            create: "Make code",
            codeLabel: "Pairing code",
            codeHelp: "Enter this code in the POS app. It works once.",
            expires: "Valid until {{time}}",
            expired: "This code has expired. Make a new one.",
            another: "Make another code",
        },
    },
    pin: {
        title: "POS PIN",
        description: "A 4–6 digit PIN to sign in at the POS, also when it is offline.",
        isSet: "Your POS PIN is set.",
        notSet: "You have no POS PIN yet.",
        set: "Set PIN",
        change: "Change PIN",
        pin: "New PIN",
        confirm: "Repeat PIN",
        mismatch: "The PINs do not match.",
        saved: "POS PIN saved. Devices get it with their next sync.",
    },
    reviews: {
        title: "POS review",
        description: "Bills and other entries from POS devices that need a look. Held ones changed nothing yet: reprocess or void them.",
        empty: "Nothing to review.",
        filters: { status: "Status", all: "All" },
        columns: {
            type: "What",
            reasons: "Why",
            reference: "Reference",
            state: "Effect",
            occurredAt: "When (device)",
            status: "Status",
        },
        applied: "Recorded",
        held: "Held",
        statuses: {
            open: "Open",
            acknowledged: "Acknowledged",
            reprocessed: "Reprocessed",
            voided: "Voided",
        },
        types: {
            SaleCompleted: "Sale",
            ReturnCompleted: "Return",
            ShiftOpened: "Shift opened",
            ShiftClosed: "Shift closed",
            CashMovementRecorded: "Cash in / out",
            CustomerCreated: "New customer",
            PosActivityRecorded: "Activity",
            unknown: "Unknown entry",
        },
        reasons: {
            STAFF_INACTIVE: "Staff member was deactivated",
            STAFF_NOT_PERMITTED: "Staff member lacks the permission here",
            APPROVAL_INVALID: "Manager approval missing or not allowed",
            RETURN_EXCEEDS_SOLD: "More returned than was sold",
            TOTALS_MISMATCH: "Device totals differ from the server's",
            PAYMENT_MISMATCH: "Payments do not add up to the bill",
            CREDIT_LIMIT_EXCEEDED: "Customer went over the credit limit",
            SHIFT_NOT_OPEN: "Shift was not open",
            SHIFT_OVERLAP: "Another shift was open on the register",
            SHIFT_TOTALS_MISMATCH: "Expected cash differs from the server's",
            DUPLICATE_NUMBER: "Bill number already used (renumbered)",
            SEQ_REUSED: "Device sent an old sequence number",
            ORIGINAL_NOT_FOUND: "Original bill not on the server yet",
            RECEIPTLESS_RETURN: "Return without a receipt",
            UNKNOWN_REFERENCE: "Unknown product, account or customer",
            DUPLICATE_ENTITY_ID: "Id already used by another entry",
            INVALID_COMMAND: "Entry could not be read",
            SCHEMA_VERSION_UNSUPPORTED: "POS app version not supported",
            PROCESSING_FAILED: "Server could not process it",
            RECOVERED: "Recovered from an old POS database",
        },
        detail: {
            title: "Review",
            device: "Device sequence",
            reference: "References",
            payload: "Entry as sent",
            note: "Note",
            notePlaceholder: "Why (needed to void)",
            resolvedBy: "Resolved",
        },
        actions: {
            acknowledge: "Acknowledge",
            reprocess: "Reprocess",
            void: "Void",
        },
        resolved: "Review resolved.",
    },
};
//# sourceMappingURL=pos.js.map